/**
 * Phone validation for WhatsApp notification numbers.
 *
 * Numbers saved in settings are handed to the Meta WhatsApp Cloud API, which
 * requires a full international number (country code + subscriber number).
 * A bare local number like "9281424400" is undeliverable, so we require an
 * explicit "+<country code>" prefix: without the "+" there is no reliable way
 * to tell "919281424400" (India + 10 digits) from a local number that merely
 * happens to be 12 digits long.
 */

/** Strip formatting humans type: spaces, dashes, dots, slashes, parentheses. */
export const normalizePhone = (raw) => (raw || '').replace(/[\s\-()./]/g, '');

/** E.164: "+", country code starting 1-9, then 8-15 digits total. */
const E164 = /^\+[1-9]\d{7,14}$/;

export const isValidPhone = (raw) => E164.test(normalizePhone(raw));

/**
 * Return a human-readable reason the number is unusable, or null when valid.
 * Messages name the fix, since the common mistake is a missing country code.
 */
export const phoneError = (raw) => {
  const value = normalizePhone(raw);

  if (!value) return 'Phone number is required';
  if (/[^\d+]/.test(value)) return 'Enter the phone number only, with no letters or extension';
  if (value.indexOf('+') > 0) return 'The + must be at the start, e.g. +14155550123 or +919876543210';

  if (!value.startsWith('+')) {
    // Overwhelmingly the miss: a local number typed without its country code.
    return 'Add your country code at the front, e.g. +1 for the US, +91 for India';
  }

  const digits = value.slice(1);
  if (digits.startsWith('0')) return 'Country code cannot start with 0, e.g. +14155550123 or +919876543210';
  if (digits.length < 8) return 'Number is too short — include your country code, e.g. +14155550123 or +919876543210';
  if (digits.length > 15) return 'Number is too long — max 15 digits including country code';
  // Countries whose numbers all have one length. A short number with a
  // valid-looking code passes E.164 but reaches nobody. The chat widget and
  // the website apply the same two rules.
  if (digits.startsWith('91') && digits.length !== 12) return 'Indian numbers have 10 digits after +91';
  if (digits.startsWith('1') && digits.length !== 11) return 'US and Canadian numbers have 10 digits after +1';

  return E164.test(value) ? null : 'Enter a valid number, e.g. +14155550123 or +919876543210';
};
